// Pure helpers for the Licenses page: source parsing, ring maths, key formatting, the history timeline.
import { daysLeft, fmtDate, parseTime, plural } from "../../format.ts";
import type { IconName } from "../../ui/index.tsx";
import type { Entitlement, Game, Redemption } from "../../../shared/types.ts";

export type SourceKind = "purchase" | "key" | "staff" | "migration" | "other";

export const SOURCE_ICON: Record<SourceKind, IconName> = { purchase: "card", key: "key", staff: "shield", migration: "refresh", other: "gift" };

/** Turns the raw `source` column ("stripe 30 Days", "key …1A2B3", "admin …", "v1 migration") into something readable. */
export function parseSource(source: string): { kind: SourceKind; label: string; detail: string } {
  const s = source.trim();
  const rest = s.replace(/^\S+\s*/, "");
  if (/^stripe\b/i.test(s)) return { kind: "purchase", label: rest ? `Purchased · ${rest}` : "Purchased", detail: rest };
  if (/^key\b/i.test(s)) return { kind: "key", label: rest ? `Key ${rest}` : "Redeemed key", detail: rest };
  if (/^admin\b/i.test(s)) return { kind: "staff", label: "Granted by staff", detail: "" };
  if (/^v1\b/i.test(s)) return { kind: "migration", label: "Migrated from v1", detail: "" };
  return { kind: "other", label: s || "Unknown source", detail: "" };
}

const DAY = 86_400_000;

/** Share of the license window still remaining (0..1). Lifetime is 1. Falls back to days/30 when the window can't be inferred. */
export function ringFraction(e: Entitlement): number {
  if (e.expires_at === null) return 1;
  const exp = parseTime(e.expires_at)?.getTime();
  if (exp === undefined) return 0;
  const left = Math.max(0, exp - Date.now());
  const from = parseTime(e.granted_at)?.getTime();
  const window = from !== undefined ? exp - from : 0;
  return Math.min(1, left / (window > 0 ? window : 30 * DAY));
}

/** Soonest-expiring active timed license, or null. */
export function nextExpiry(list: Entitlement[]): { entitlement: Entitlement; days: number } | null {
  let best: Entitlement | null = null;
  for (const e of list) {
    if (!e.active || e.expires_at === null) continue;
    if (!best || (parseTime(e.expires_at)?.getTime() ?? Infinity) < (parseTime(best.expires_at)?.getTime() ?? Infinity)) best = e;
  }
  return best ? { entitlement: best, days: Math.max(0, daysLeft(best.expires_at!)) } : null;
}

export const dayLabel = (days: number) => (days <= 1 ? "today" : `${days} days`);

/**
 * Formats input as EXO-XXXXX-XXXXX-XXXXX-XXXXX while typing or pasting.
 * No trailing dash is added, so backspace works naturally. The Crockford alphabet has no O, so a leading "EXO" is always the prefix.
 */
export function formatKey(raw: string): string {
  const clean = raw.toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (!clean) return "";
  if (clean.length <= 3 && "EXO".startsWith(clean)) return clean;
  const body = (clean.startsWith("EXO") ? clean.slice(3) : clean).slice(0, 20);
  if (!body) return "EXO";
  return `EXO-${body.match(/.{1,5}/g)!.join("-")}`;
}

/** Cheapest tier's price string, for "From £x". */
export function fromPrice(g: Game): string | null {
  const num = (p: string) => { const m = /\d[\d,]*\.?\d*/.exec(p); return m ? parseFloat(m[0].replace(/,/g, "")) : NaN; };
  let best = g.pricing_tiers[0];
  if (!best) return null;
  for (const t of g.pricing_tiers) if (num(t.price) < num(best.price)) best = t;
  return best.price;
}

export interface TimelineItem { id: string; at: string; kind: SourceKind; title: string; detail: string }

/** Redemptions plus every non-key grant, newest first. Key grants are skipped because the redemption already covers them. */
export function buildTimeline(redemptions: Redemption[], entitlements: Entitlement[], gameName: (id: string) => string): TimelineItem[] {
  const items: TimelineItem[] = redemptions.map((r) => ({
    id: `r${r.id}`,
    at: r.redeemed_at,
    kind: "key",
    title: `Redeemed key ${r.key_hint}`,
    detail: `${r.games.map(gameName).join(", ") || "No games"} · ${r.days === null ? "Lifetime" : plural(r.days, "day")}`,
  }));
  for (const e of entitlements) {
    const src = parseSource(e.source);
    if (src.kind === "key") continue;
    items.push({
      id: `g${e.game_id}`,
      at: e.granted_at,
      kind: src.kind,
      title: src.kind === "purchase" ? `Purchased ${src.detail || "license"}` : src.label,
      detail: `${e.game_name} · ${e.expires_at === null ? "Lifetime" : `until ${fmtDate(e.expires_at)}`}`,
    });
  }
  return items.sort((a, b) => (parseTime(b.at)?.getTime() ?? 0) - (parseTime(a.at)?.getTime() ?? 0));
}
